import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "recently_viewed";
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const safeParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const normalizeDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const computeDurationLabel = (startDate, endDate) => {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);
  if (!start || !end) return "";
  const diff = Math.round((end - start) / MS_PER_DAY);
  return String(Math.max(1, diff + 1));
};

const buildVisitedTrip = (trip) => {
  if (!trip) return null;
  return {
    id: trip.id ?? trip.tripPublicationId ?? "",
    establishmentId: trip.establishmentId ?? "",
    tripDateId: trip.tripDateId ?? "",
    title: trip.title ?? "",
    picture:
      trip.picture ??
      trip?.pictures?.[0]?.link ??
      trip?.pictures?.[0] ??
      "",
    price: {
      currency: trip?.price?.currency ?? "",
      value: trip?.price?.value ?? "",
    },
    durationLabel: computeDurationLabel(trip?.startDate, trip?.endDate),
    isFavorite: Boolean(trip?.isFavorite),
  };
};

const encodeTrip = (trip) => {
  const json = JSON.stringify(trip);
  return btoa(unescape(encodeURIComponent(json)));
};

const decodeTrip = (encoded) => {
  if (!encoded) return null;
  if (typeof encoded === "object") return encoded;
  try {
    const json = decodeURIComponent(escape(atob(encoded)));
    const parsed = safeParse(json, null);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const readEncodedMap = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = safeParse(raw, {});
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return parsed;
  } catch {
    return {};
  }
};

const writeEncodedMap = (encodedMap) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(encodedMap));
  } catch {
    // ignore storage errors
  }
};

/* L'historique vit dans le navigateur, sans lien avec un compte : on le vide
   à la déconnexion pour que la personne suivante sur le même poste ne voie
   pas les voyages consultés. L'événement prévient les composants de l'onglet
   courant (« storage » ne se déclenche que dans les autres onglets). */
const CLEARED_EVENT = "recently_viewed:cleared";

export const clearVisitedTrips = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
  window.dispatchEvent(new Event(CLEARED_EVENT));
};

/* 8 voyages : la bande de l'accueil affiche 5 cartes par ligne sur un écran
   courant, le reste se voit au défilement (ou sur une seconde ligne). */
export const useVisitedTripStorage = (trip, { max = 8 } = {}) => {
  const addVisitedTrip = useCallback(
    (nextTrip) => {
      const payload = buildVisitedTrip(nextTrip);
      if (!payload || !payload.id) return;
      const encoded = encodeTrip(payload);
      const existing = readEncodedMap();
      const keys = Object.keys(existing).filter((id) => id !== payload.id);
      const orderedIds = [payload.id, ...keys].slice(0, max);
      const nextMap = orderedIds.reduce((acc, id) => {
        acc[id] = id === payload.id ? encoded : existing[id];
        return acc;
      }, {});
      writeEncodedMap(nextMap);
    },
    [max]
  );

  useEffect(() => {
    if (!trip) return;
    addVisitedTrip(trip);
  }, [trip, addVisitedTrip]);

  return { addVisitedTrip };
};

export const useRecentVisitedTrips = () => {
  const [trips, setTrips] = useState(() => {
    const encodedMap = readEncodedMap();
    return Object.values(encodedMap).map(decodeTrip).filter(Boolean);
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event) => {
      if (event?.key && event.key !== STORAGE_KEY) return;
      const encodedMap = readEncodedMap();
      setTrips(Object.values(encodedMap).map(decodeTrip).filter(Boolean));
    };
    window.addEventListener("storage", handler);
    window.addEventListener(CLEARED_EVENT, handler);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener(CLEARED_EVENT, handler);
    };
  }, []);

  const refresh = () => {
    const encodedMap = readEncodedMap();
    setTrips(Object.values(encodedMap).map(decodeTrip).filter(Boolean));
  };

  return { trips, refresh, clear: clearVisitedTrips };
};
